import React, {useEffect, useState} from 'react';
import {TableBodyProps} from '@src/@types/components/List';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import classnames from '@src/helpers/classnames';
import {AdditionalRowControllerProps, TAdditionalProps} from '@src/@types/components/ControlledList';

export default function AdditionalRowController<
        Model extends IEmptyModel,
        AdditionalProps extends TAdditionalProps<Model> = Record<string, never>
>(props: AdditionalRowControllerProps<Model, AdditionalProps>) {
    const {
        additionalProps,
        customColumnsComponents,
        addControlToColumnName = 'id',
        controlButtonClassName = 'btn btn-sm btn-outline-primary',
        disabled,
        forceExpanded,
    } = props;

    const [isExpanded, setIsExpanded] = useState(
            props.properties.model?.defaultCardState != null ?
                    props.properties.model?.defaultCardState === 'expanded' :
                    props.properties.model?.id == null
    );

    useEffect(() => {
        if (forceExpanded != null) {
            setIsExpanded(
                    typeof forceExpanded === 'boolean' ?
                            forceExpanded :
                            forceExpanded(props.properties.model)
            );
        }
    }, [forceExpanded, props.properties.model]);

    const cellRenderer: TableBodyProps<Model>['renderCellContent'] = (props, baseRenderer) => {
        if (customColumnsComponents != null && props.config.name in customColumnsComponents) {
            const Component: any = customColumnsComponents[props.config.name];

            return <Component {...additionalProps} {...props}/>;
        }

        if (props.config.name !== addControlToColumnName) {
            return baseRenderer();
        }

        return <>
            {baseRenderer()}

            <button
                    type="button"
                    className={classnames(controlButtonClassName, 'ms-2')}
                    disabled={disabled}
                    onClick={() => {
                        setIsExpanded(was => !was);
                    }}
            >
                <i className={classnames({
                    'fa fa-chevron-up': isExpanded,
                    'fa fa-chevron-down': !isExpanded
                })}/>
            </button>
        </>;
    };

    const contentRenderer: TableBodyProps<Model>['renderRowContent'] = (properties) => {
        return <>
            {properties.cellBeforeColumnsComponent}

            {properties.renderColumns(cellRenderer)}

            {properties.renderControlsColumn()}
        </>;
    };

    if (!isExpanded) {
        return <>{props.baseRenderer(undefined, contentRenderer)}</>;
    }

    const AdditionalRow: any = props.additionalRowComponent;

    return <>
        {props.baseRenderer(undefined, contentRenderer)}

        <tr className="models-list__content-table__extra-row">
            <td colSpan={props.properties.context?.columnsNumber}>
                <AdditionalRow {...additionalProps} {...props.properties}/>
            </td>
        </tr>
    </>;
}
